import React, { useState } from "react";

import { Gauge, LayoutGrid, ListTree, Scale } from "lucide-react";

import RoDimensionsPanel from "./RoDimensionsPanel";
import RoMetricsPanel from "./RoMetricsPanel";
import RoScoresPanel from "./RoScoresPanel";
import RoWeightsPanel from "./RoWeightsPanel";
import TabsComponent from "../../../Components/Tab";

type RoConfigTab = "dimensions" | "metrics" | "weights" | "scores";

const roConfigTabs = [
  { id: "dimensions" as const, label: "R&O Dimensions" },
  { id: "metrics" as const, label: "R&O Metrics" },
  { id: "weights" as const, label: "R&O Weights" },
  { id: "scores" as const, label: "R&O Scores" },
];

const TAB_META: Record<
  RoConfigTab,
  { title: string; description: string; icon: React.ReactNode }
> = {
  dimensions: {
    title: "R&O Dimensions",
    description:
      "Risk & Opportunity dimensions for this tenant, grouped by Risk and Opportunity. Edit include/exclude and weights on the R&O Weights tab.",
    icon: <ListTree size={20} />,
  },
  metrics: {
    title: "R&O Metrics",
    description:
      "Metrics mapped to each R&O dimension from this tenant’s local config. Edit membership weights on the R&O Weights tab.",
    icon: <LayoutGrid size={20} />,
  },
  weights: {
    title: "R&O Weights",
    description:
      "Edit relative weights and include/exclude for dimensions and KPIs. Add or remove metrics here too. Saved locally — re-run Data Hub R&O scores to apply.",
    icon: <Scale size={20} />,
  },
  scores: {
    title: "R&O Scores",
    description:
      "Risk Composite Score (RCS) and Opportunity Composite Score (OCS), plus the per-dimension scores behind them.",
    icon: <Gauge size={20} />,
  },
};

const RoConfigSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RoConfigTab>("dimensions");
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const meta = TAB_META[activeTab];

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-5">
        <TabsComponent
          tabs={roConfigTabs}
          activeTab={activeTab}
          onTabChange={id => {
            setHeaderActions(null);
            setActiveTab(id as RoConfigTab);
          }}
          variant="tab-underline"
          className="mb-0"
        />
      </div>
      <div className="px-5 pb-5 pt-4">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <h4 className="mb-0 flex items-center gap-2 text-lg font-semibold text-gray-900">
            {meta.icon}
            {meta.title}
          </h4>
          {activeTab === "weights" && headerActions}
        </div>
        <p className="mb-4 text-sm text-gray-600">{meta.description}</p>
        {activeTab === "dimensions" && <RoDimensionsPanel />}
        {activeTab === "metrics" && <RoMetricsPanel />}
        {activeTab === "weights" && (
          <RoWeightsPanel onHeaderActionsChange={setHeaderActions} />
        )}
        {activeTab === "scores" && <RoScoresPanel />}
      </div>
    </div>
  );
};

export default RoConfigSection;
